import { useMemo } from 'react';
import { t } from '@lingui/macro';
import { makeStyles } from '@material-ui/core';
import moment from 'moment';
import { isEmpty, isNil, merge } from 'lodash';
import { getLogger } from 'loglevel';

import { SUCCESS, createToastConfig } from '../../../stores/ToastStore';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { useDataService } from '../../../services/ApiDataService';
import { SERVER_ERROR_MESSAGE, convertLoadParamsToQueryParams, convertQueryResponse } from '../../data-table/Loaders';
import useAlerts from '../../../stores/AlertsStore';

const log = getLogger('Maintenance Window Utils');

export const useMaintenanceWindowFormStyles = makeStyles((theme) => ({
    selectorWrapper: {
        marginTop: '16px',
    },
    detailsInfoWrapper: {
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    detailsInfoIcon: {
        color: theme.palette.alert.info,
        fontSize: 24,
    },
    fieldInfoWrapper: {
        margin: '24px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
}));

/**
 * Custom loader that queries and appends device count to maintenance window results
 * @returns loader to be used to query maintenance windows with their device counts
 */
export function useEntityWithDeviceCountLoader(dataSource, selected, isInverted) {
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();

    return useMemo(() => {
        if (!dataService) return null;

        return async (loaderParams) => {
            const { url, params: datasourceParams, body } = dataSource;
            const queryParams = merge({}, datasourceParams, convertLoadParamsToQueryParams(loaderParams));

            return dataService
                .post(
                    url,
                    {
                        selection: selected,
                        inverted: isInverted,
                        ...(body || {}),
                    },
                    queryParams
                )
                .then(async (response) => {
                    const entitiesWithDeviceCounts = await Promise.all(
                        response.data.map(async (entity) => {
                            let deviceCount = 0;
                            if (!isNil(entity?.rules) && !isEmpty(entity?.rules)) {
                                const { devices } = await dataService.post(
                                    '/ipm/v1/admin/sites/matchedDevices',
                                    {
                                        rules: entity?.rules || [],
                                    },
                                    {}
                                );

                                deviceCount = devices.length;
                            }

                            return { ...entity, deviceCount };
                        })
                    );

                    return convertQueryResponse(dataSource, { ...response, data: entitiesWithDeviceCounts });
                })
                .catch((error) => {
                    log.error(error);

                    alertsActions.createErrorAlert(SERVER_ERROR_MESSAGE);

                    return convertQueryResponse(dataSource, null);
                });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataService, dataSource, selected]);
}

/**
 * Function that deletes maintenance windows via the v3 api
 * @param {string[]} maintenanceWindowIds array of maintenance window uuids to delete
 * @param {Object} dataService data service object used to make api calls
 * @param {Function} dataRefresher refresher method to reload table data after delete
 * @param {Function} handleGenericError handle generic error method used on fail maintenance window deletion
 */
export async function deleteSelectedMaintenanceWindows(
    maintenanceWindowIds,
    dataService,
    dataRefresher,
    handleGenericError,
    uiToastActions
) {
    const successToast = createToastConfig(SUCCESS, t`Successfully Deleted`);
    const deleteRequests = [];

    maintenanceWindowIds.forEach((maintenanceWindowId) => {
        deleteRequests.push(dataService.post(`/api/v3/maintenance-windows/${maintenanceWindowId}/delete`, {}));
    });

    try {
        await Promise.all(deleteRequests);
        uiToastActions.createToast(successToast);
    } catch (e) {
        // Set the response body to the error message to correctly display it in the warning banner
        e.responseBody = e.responseBody?.errors?.at(0)?.message || undefined;
        handleGenericError(e);
    }

    if (dataRefresher) dataRefresher();
}

/**
 * Creates a string used to display entities associated with a maintenance window in the
 * maintenance windows list
 * @param {{id: string, name: string}[]} entityList array of entities
 * @returns list of comma separated entity names as a string
 */
export function formatMaintenanceEntityList(entityList) {
    if (Array.isArray(entityList) && entityList.length > 0) {
        return entityList.map((entity) => entity.name).join(', ');
    }

    return DEFAULT_INVALID_VALUE;
}

/**
 * Validates that the passed in value is in the future
 * @param {string | moment} value value to validate. Can be an ISO string or a moment object
 * @param {string} errorMessage error message to display if the date time is not in the future
 * @param {string} timezone users timezone to be used when validating. Use the useTimezone hook to get this value
 * @returns true if valid or the errorMessage if invalid
 */
export function isValidFutureDateTime(value, errorMessage, timezone) {
    value = moment.isMoment(value) ? value.tz(timezone) : moment.utc(value).tz(timezone);

    return value.valueOf() > moment().valueOf() || t`${errorMessage}`;
}

/**
 * Validates that he passed startTime is before the passed endTime
 * @param {string | moment} startTime start time to check. Can be an ISO string or a moment object
 * @param {string | moment} endTime end time to check. Can be an ISO string or a moment object
 * @param {string} errorMessage error message to display if the start time is not before the end time
 * @param {string} timezone users timezone to be used when validating. use the useTimezone hook to get this value
 * @returns true if valid or the errorMessage if invalid
 */
export function isValidEndTimeAfterStartTime(startTime, endTime, errorMessage, timezone) {
    startTime = moment.isMoment(startTime) ? startTime.tz(timezone) : moment.utc(startTime).tz(timezone);
    endTime = moment.isMoment(endTime) ? endTime.tz(timezone) : moment.utc(endTime).tz(timezone);

    return startTime.valueOf() < endTime.valueOf() || t`${errorMessage}`;
}
